import { IUserRepository } from '../repository/interfaces';
import { SubscriptionRepository } from '../repository/subscription.repository';
import { GetUserData } from '../repository/interfaces';
import { ISubscriptionUseCase } from './interfaces';

/**
 * Subscription Use Case
 * Handles subscription management, billing, and feature access
 * User Stories: 3, 4
 * Uses both SubscriptionRepository (for DB/Stripe operations) and UserRepository (for user data)
 */
export class SubscriptionUseCase  implements ISubscriptionUseCase {
  constructor(
    private subscriptionRepo: SubscriptionRepository,
    private userRepo: IUserRepository
  ) {
    // Removed BaseUseCase
  }

  /**
   * Upgrade or change subscription plan
   * User Story 3: As a user, I can upgrade/downgrade/cancel a plan with coupon support
   */
  async changeSubscription(
    userId: string,
    newPlan: {
      tier: 'free' | 'basic' | 'professional' | 'enterprise';
      frequency: 'monthly' | 'yearly';
      couponCode?: string;
    }
  ): Promise<{
    user: GetUserData;
    subscription: any;
    paymentRequired: boolean;
    proratedAmount?: number;
    nextBillingDate: Date;
  }> {
    console.log('[SubscriptionUseCase.changeSubscription] START - UserId:', userId, 'New plan:', newPlan.tier, newPlan.frequency, 'Coupon:', newPlan.couponCode);
    try {
      console.log('[SubscriptionUseCase.changeSubscription] Getting current user');
      // Get current user
      const user = await this.userRepo.getUserById(userId);
      if (!user) {
        console.log('[SubscriptionUseCase.changeSubscription] ERROR: User not found');
        throw new Error('User not found');
      }
      console.log('[SubscriptionUseCase.changeSubscription] Current tier:', user.subscriptionTier, '->', newPlan.tier);

      // Validate plan change
      if (user.subscriptionTier === newPlan.tier) {
        console.log('[SubscriptionUseCase.changeSubscription] ERROR: Already on this tier');
        throw new Error('User is already on this subscription tier');
      }

      // Calculate pricing
      const pricing = this.getPlanPricing(newPlan.tier, newPlan.frequency);
      let finalPrice = pricing.price;

      // Apply coupon if provided
      let couponDiscount = 0;
      if (newPlan.couponCode) {
        couponDiscount = await this.validateAndApplyCoupon(newPlan.couponCode, newPlan.tier);
        finalPrice = Math.max(0, finalPrice - couponDiscount);
      }

      // Calculate proration for mid-cycle changes
      let proratedAmount = finalPrice;
      const nextBillingDate = new Date();

      if (user.subscriptionTier && user.subscriptionTier !== 'free') {
        const daysRemaining = this.calculateDaysRemaining(user);
        const totalDays = newPlan.frequency === 'yearly' ? 365 : 30;
        proratedAmount = (finalPrice * daysRemaining) / totalDays;
      }

      // Set next billing date
      if (newPlan.frequency === 'yearly') {
        nextBillingDate.setFullYear(nextBillingDate.getFullYear() + 1);
      } else {
        nextBillingDate.setMonth(nextBillingDate.getMonth() + 1);
      }

      // Process payment if required
      const paymentRequired = finalPrice > 0;

      if (paymentRequired) {
        // In a real implementation, you'd process payment here
        const paymentSuccess = await this.processPayment(userId, proratedAmount);

        if (!paymentSuccess) {
          throw new Error('Payment processing failed');
        }
      }

      // Update user subscription
      const updatedUser = await this.userRepo.updateUser(userId, {
        subscriptionTier: newPlan.tier,
        frequency: newPlan.frequency,
        updatedAt: new Date()
      });

      // Create subscription record (mock)
      const subscription = {
        id: `sub_${Date.now()}`,
        userId: userId,
        tier: newPlan.tier,
        frequency: newPlan.frequency,
        price: finalPrice,
        couponUsed: newPlan.couponCode,
        discountApplied: couponDiscount,
        nextBillingDate: nextBillingDate,
        status: 'active',
        createdAt: new Date()
      };

      // Update feature flags immediately
      await this.updateFeatureFlags(userId, newPlan.tier);

      const result = {
        user: updatedUser,
        subscription,
        paymentRequired,
        proratedAmount: paymentRequired ? proratedAmount : undefined,
        nextBillingDate
      };
      console.log('[SubscriptionUseCase.changeSubscription] SUCCESS - New tier:', newPlan.tier, 'Payment required:', paymentRequired, 'Amount:', proratedAmount);
      return result;
    } catch (error) { 
      console.error('[SubscriptionUseCase.changeSubscription] ERROR:', error); 
      throw error; 
    }
  }

  /**
   * Cancel subscription
   * User Story 3: As a user, I can upgrade/downgrade/cancel a plan
   */
  async cancelSubscription(
    userId: string,
    options: {
      immediate: boolean;
      reason?: string;
      feedback?: string;
    }
  ): Promise<{
    user: GetUserData;
    cancellation: any;
    refundAmount?: number;
    accessUntil: Date;
  }> {
    console.log('[SubscriptionUseCase.cancelSubscription] START - UserId:', userId, 'Immediate:', options.immediate, 'Reason:', options.reason);
    try {
      console.log('[SubscriptionUseCase.cancelSubscription] Getting user details');
      // Get current user
      const user = await this.userRepo.getUserById(userId);
      if (!user) {
        throw new Error('User not found');
      }

      // Check if user has an active subscription
      if (!user.subscriptionTier || user.subscriptionTier === 'free') {
        throw new Error('User does not have an active subscription to cancel');
      }

      // Calculate refund if applicable
      let refundAmount: number | undefined;
      let accessUntil = new Date();

      if (options.immediate) {
        // Immediate cancellation - refund unused portion
        const daysRemaining = this.calculateDaysRemaining(user);
        const dailyRate = this.getPlanPricing(user.subscriptionTier, user.frequency).price /
                         (user.frequency === 'yearly' ? 365 : 30);
        refundAmount = dailyRate * daysRemaining;
        accessUntil = new Date(); // Immediate cancellation
      } else {
        // End of billing cycle cancellation
        accessUntil = this.calculateNextBillingDate(user);
      }

      // Process refund if applicable
      if (refundAmount && refundAmount > 0) {
        await this.processRefund(userId, refundAmount);
      }

      // Downgrade to free tier
      const updatedUser = await this.userRepo.updateUser(userId, {
        subscriptionTier: 'free',
        frequency: null,
        updatedAt: new Date()
      });

      // Update feature flags
      await this.updateFeatureFlags(userId, 'free');

      // Create cancellation record
      const cancellation = {
        id: `cancel_${Date.now()}`,
        userId: userId,
        previousTier: user.subscriptionTier,
        cancelledAt: new Date(),
        effectiveDate: accessUntil,
        reason: options.reason,
        feedback: options.feedback,
        refundAmount: refundAmount,
        immediate: options.immediate
      };

      const result = {
        user: updatedUser,
        cancellation,
        refundAmount,
        accessUntil
      };
      console.log('[SubscriptionUseCase.cancelSubscription] SUCCESS - Cancelled, Refund:', refundAmount, 'Access until:', accessUntil);
      return result;
    } catch (error) { 
      console.error('[SubscriptionUseCase.cancelSubscription] ERROR:', error); 
      throw error; 
    }
  }

  /**
   * Process successful payment webhook
   * User Story 4: As the system, on successful payment I grant role/feature flags immediately (idempotent)
   */
  async processSuccessfulPayment(
    paymentData: {
      userId: string;
      subscriptionId: string;
      amount: number;
      currency: string;
      paymentMethod: string;
      transactionId: string;
    }
  ): Promise<{
    user: GetUserData;
    subscription: any;
    featuresGranted: string[];
    notificationsSent: boolean;
  }> {
    console.log('[SubscriptionUseCase.processSuccessfulPayment] START - UserId:', paymentData.userId, 'Amount:', paymentData.amount, 'Transaction:', paymentData.transactionId);
    try {
      console.log('[SubscriptionUseCase.processSuccessfulPayment] Verifying payment data');
      // Verify payment data
      if (!paymentData.userId || !paymentData.subscriptionId) {
        throw new Error('Invalid payment data: missing user or subscription ID');
      }

      // Get user
      const user = await this.userRepo.getUserById(paymentData.userId);
      if (!user) {
        throw new Error('User not found');
      }

      // Verify this isn't a duplicate payment (idempotent)
      // In a real implementation, you'd check payment records

      // Update subscription status (mock - would update actual subscription record)
      const subscription = {
        id: paymentData.subscriptionId,
        userId: paymentData.userId,
        status: 'active',
        lastPaymentDate: new Date(),
        lastPaymentAmount: paymentData.amount,
        payment_method: paymentData.paymentMethod,
        transactionId: paymentData.transactionId
      };

      // Grant feature flags immediately
      const featuresGranted = await this.updateFeatureFlags(
        paymentData.userId,
        user.subscriptionTier || 'free'
      );

      // Send confirmation notifications
      const notificationsSent = await this.sendPaymentConfirmation(
        paymentData.userId,
        paymentData.amount,
        paymentData.currency
      );

      const result = {
        user,
        subscription,
        featuresGranted,
        notificationsSent
      };
      console.log('[SubscriptionUseCase.processSuccessfulPayment] SUCCESS - Features granted:', featuresGranted.length, 'Notifications sent:', notificationsSent);
      return result;
    } catch (error) { 
      console.error('[SubscriptionUseCase.processSuccessfulPayment] ERROR:', error); 
      throw error; 
    }
  }

  /**
   * Get subscription details and usage
   */
  async getSubscriptionDetails(
    userId: string
  ): Promise<{
    user: GetUserData;
    subscription: any;
    usage: {
      features: Record<string, { used: number; limit: number; percentage: number }>;
      billing: {
        nextBillingDate: Date;
        amount: number;
        currency: string;
      };
    };
    canUpgrade: boolean;
    availablePlans: Array<{
      tier: string;
      frequency: string;
      price: number;
      features: string[];
    }>;
  }> {
    try {
      // Get user
      const user = await this.userRepo.getUserById(userId);
      if (!user) {
        throw new Error('User not found');
      }

      // Get subscription details (mock)
      const subscription = {
        id: `sub_${userId}`,
        tier: user.subscriptionTier || 'free',
        frequency: user.frequency,
        status: user.subscriptionTier === 'free' ? 'inactive' : 'active',
        startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), // 30 days ago
        current_period_start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
        current_period_end: this.calculateNextBillingDate(user)
      };

      // Get feature usage (mock)
      const tierLimits = this.getTierLimits(user.subscriptionTier || 'free');
      const usage = {
        features: {
          courses: { used: 5, limit: tierLimits.courses, percentage: 50 },
          resources: { used: 10, limit: tierLimits.resources, percentage: 33 },
          requests: { used: 3, limit: tierLimits.requests, percentage: 30 },
          storage: { used: 500, limit: tierLimits.storage, percentage: 25 }
        },
        billing: {
          nextBillingDate: this.calculateNextBillingDate(user),
          amount: this.getPlanPricing(user.subscriptionTier || 'free', user.frequency).price,
          currency: 'USD'
        }
      };

      // Check upgrade eligibility
      const canUpgrade = user.subscriptionTier !== 'enterprise';

      // Get available plans
      const availablePlans = this.getAvailablePlans();

      return {
        user,
        subscription,
        usage,
        canUpgrade,
        availablePlans
      };
    } catch (error) { console.error("Error:", error); throw error; }
  }

  /**
   * Apply coupon to subscription
   */
  async applyCoupon(
    userId: string,
    couponCode: string,
    planTier: string
  ): Promise<{
    discount: number;
    discountType: 'percentage' | 'fixed';
    finalPrice: number;
    valid: boolean;
    message: string;
  }> {
    try {
      // Validate coupon
      const couponValidation = await this.validateCoupon(couponCode, planTier, userId);

      if (!couponValidation.valid) {
        return {
          discount: 0,
          discountType: 'fixed',
          finalPrice: this.getPlanPricing(planTier, 'monthly').price,
          valid: false,
          message: couponValidation.message
        };
      }

      // Calculate discount
      const originalPrice = this.getPlanPricing(planTier, 'monthly').price;
      let discountAmount = 0;

      if (couponValidation.discountType === 'percentage') {
        discountAmount = (originalPrice * couponValidation.discountValue) / 100;
      } else {
        discountAmount = couponValidation.discountValue;
      }

      const finalPrice = Math.max(0, originalPrice - discountAmount);

      return {
        discount: discountAmount,
        discountType: couponValidation.discountType,
        finalPrice,
        valid: true,
        message: `Coupon applied: ${couponValidation.discountValue}${couponValidation.discountType === 'percentage' ? '%' : '$'} off`
      };
    } catch (error) { console.error("Error:", error); throw error; }
  }

  /**
   * Handle subscription renewal
   */
  async processSubscriptionRenewal(
    userId: string
  ): Promise<{
    success: boolean;
    user: GetUserData;
    renewal: any;
    nextBillingDate: Date;
  }> {
    try {
      // Get user
      const user = await this.userRepo.getUserById(userId);
      if (!user) {
        throw new Error('User not found');
      }

      if (!user.subscriptionTier || user.subscriptionTier === 'free') {
        throw new Error('User does not have a renewable subscription');
      }

      // Calculate renewal amount
      const pricing = this.getPlanPricing(user.subscriptionTier, user.frequency);
      const renewalAmount = pricing.price;

      // Process payment
      const paymentSuccess = await this.processPayment(userId, renewalAmount);

      if (!paymentSuccess) {
        // Handle failed renewal - could downgrade to free or send notification
        throw new Error('Renewal payment failed');
      }

      // Calculate next billing date
      const nextBillingDate = this.calculateNextBillingDate(user);

      // Create renewal record
      const renewal = {
        id: `renewal_${Date.now()}`,
        userId: userId,
        amount: renewalAmount,
        billingDate: new Date(),
        nextBillingDate: nextBillingDate,
        status: 'completed'
      };

      // Ensure feature flags are still active
      await this.updateFeatureFlags(userId, user.subscriptionTier);

      return {
        success: true,
        user,
        renewal,
        nextBillingDate
      };
    } catch (error) { console.error("Error:", error); throw error; }
  }

  // Helper methods

  private async getPlanPricing(tier: string, frequency: string) {
    console.log('[SubscriptionUseCase.getPlanPricing] Getting pricing for:', tier, frequency);
    
    try {
      // Get plan from repository with dynamic pricing from Stripe
      const plans = await this.subscriptionRepository.getSubscriptionPlans();
      const plan = plans.find(p => p.tier === tier);
      
      if (!plan) {
        console.warn('[SubscriptionUseCase.getPlanPricing] Plan not found for tier:', tier);
        return {
          price: 0,
          originalPrice: 0,
          yearlyPrice: 0
        };
      }
      
      return {
        price: plan.price,
        originalPrice: plan.price,
        yearlyPrice: plan.price // Since we're now using single price per plan
      };
    } catch (error) {
      console.error('[SubscriptionUseCase.getPlanPricing] Error fetching pricing:', error);
      // Fallback to free tier pricing
      return {
        price: 0,
        originalPrice: 0,
        yearlyPrice: 0
      };
    }
  }

  private getTierLimits(tier: string) {
    const limits: Record<string, any> = {
      free: { courses: 1, resources: 5, requests: 2, storage: 100 },
      basic: { courses: 5, resources: 25, requests: 10, storage: 1000 },
      professional: { courses: 25, resources: 100, requests: 50, storage: 5000 },
      enterprise: { courses: -1, resources: -1, requests: -1, storage: 50000 }
    };

    return limits[tier] || limits.free;
  }

  private getAvailablePlans() {
    return [
      {
        tier: 'basic',
        frequency: 'monthly',
        price: 9.99,
        features: ['5 courses', '25 resources', '10 requests/month', '1GB storage']
      },
      {
        tier: 'professional',
        frequency: 'monthly',
        price: 19.99,
        features: ['25 courses', '100 resources', '50 requests/month', '5GB storage']
      },
      {
        tier: 'enterprise',
        frequency: 'monthly',
        price: 49.99,
        features: ['Unlimited courses', 'Unlimited resources', 'Unlimited requests', '50GB storage']
      }
    ];
  }

  private async validateAndApplyCoupon(couponCode: string, tier: string): Promise<number> {
    // Mock coupon validation
    const validCoupons: Record<string, { discount: number; validTiers: string[] }> = {
      'WELCOME10': { discount: 10, validTiers: ['basic', 'professional'] },
      'PRO20': { discount: 20, validTiers: ['professional', 'enterprise'] },
      'SAVE50': { discount: 50, validTiers: ['enterprise'] }
    };

    const coupon = validCoupons[couponCode.toUpperCase()];
    if (!coupon || !coupon.validTiers.includes(tier)) {
      return 0;
    }

    return coupon.discount;
  }

  private async validateCoupon(couponCode: string, tier: string, userId: string) {
    // Mock coupon validation with more details
    const coupon = await this.validateAndApplyCoupon(couponCode, tier);

    return {
      valid: coupon > 0,
      discountValue: coupon,
      discountType: 'percentage' as const,
      message: coupon > 0 ? 'Valid coupon' : 'Invalid or expired coupon'
    };
  }

  private calculateDaysRemaining(user: GetUserData): number {
    // Mock calculation - in reality would use subscription start date
    return Math.floor(Math.random() * 30); // Random days remaining
  }

  private calculateNextBillingDate(user: GetUserData): Date {
    const nextDate = new Date();
    if (user.frequency === 'yearly') {
      nextDate.setFullYear(nextDate.getFullYear() + 1);
    } else {
      nextDate.setMonth(nextDate.getMonth() + 1);
    }
    return nextDate;
  }

  private async processPayment(userId: string, amount: number): Promise<boolean> {
    // Mock payment processing - 95% success rate
    console.log(`[SubscriptionUseCase.processPayment] Processing payment of $${amount} for user ${userId}`);
    await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate processing time
    const success = Math.random() > 0.05;
    console.log(`[SubscriptionUseCase.processPayment] Payment result:`, success);
    return success;
  }

  private async processRefund(userId: string, amount: number): Promise<void> {
    // Mock refund processing
    this.logInfo(`Processing refund of $${amount} for user ${userId}`);
    await new Promise(resolve => setTimeout(resolve, 500));
  }

  private async updateFeatureFlags(userId: string, tier: string): Promise<string[]> {
    // Mock feature flag updates based on tier
    const features: Record<string, string[]> = {
      free: ['basicAccess'],
      basic: ['basicAccess', 'createResources', 'requestProfessionals'],
      professional: ['basicAccess', 'createResources', 'requestProfessionals', 'createCourses', 'analytics'],
      enterprise: ['basicAccess', 'createResources', 'requestProfessionals', 'createCourses', 'analytics', 'adminTools', 'unlimitedStorage']
    };

    const grantedFeatures = features[tier] || features.free;
    console.log(`[SubscriptionUseCase.updateFeatureFlags] Updated feature flags for user ${userId}: ${grantedFeatures.join(', ')}`);

    return grantedFeatures;
  }

  private async sendPaymentConfirmation(userId: string, amount: number, currency: string): Promise<boolean> {
    // Mock notification sending
    this.logInfo(`Payment confirmation sent to user ${userId} for ${currency}${amount}`);
    return true;
  }
}
